/* ==============================================================================

 CORE.SECURITY MODULE

 Contains all security related code

 ============================================================================= */
angular.module('core.security', ['core.ajax', 'services.globalizations']);

/* ==============================================================================

 AUTH INTERCEPTOR

 Adds the Auth Token to the Http headers collection
 if the token is present.

 ============================================================================= */
angular.module('core.security').factory('AuthInterceptor', ['$q', '$rootScope', '$location', '$interval', '$injector', function ($q, $rootScope, $location, $interval, $injector)
{
    function logout()
    {
        stopTokenRefreshInterval();
        $rootScope.$emit('user:log-out');
    }
    
    // Listen for logout events from other parts of the app
    $rootScope.$on('user:logged-out', function() {
        stopTokenRefreshInterval();
    });
    
    // Setup token refresh interval (15 minutes = 900000ms)
    var TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; 
    var tokenRefreshIntervalId = null;
    
    function startTokenRefreshInterval() {
        // Only start the interval if it's not already running
        if (tokenRefreshIntervalId === null) {
            tokenRefreshIntervalId = $interval(function() {
                refreshToken();
            }, TOKEN_REFRESH_INTERVAL);
        }
    }
    
    function stopTokenRefreshInterval() {
        if (tokenRefreshIntervalId !== null) {
            $interval.cancel(tokenRefreshIntervalId);
            tokenRefreshIntervalId = null;
        }
    }
    
    function refreshToken(forceRefresh) {
        // Only refresh if user is active or force refresh is requested
        if ($rootScope.userActive || forceRefresh === true) {
            // Use $injector to get Authentication service to avoid circular dependency
            var Authentication = $injector.get('Authentication');
            return Authentication.refreshToken(forceRefresh)
                .catch(function(error) {
                    logout();
                    return $q.reject(error);
                });
        }
    }

    return {
        request: function (config)
        {
            config.headers = config.headers || {};

            // Get the current URL to create a unique cookie name
            var hostname = $location.host() || 'default';
            var cookieName = hostname + '.jwt.token';

            // If the auth token is present in cookies
            var token = Cookies.get(cookieName);
            if (token !== undefined)
            {
                // Add it to the headers using Bearer scheme for JWT
                config.headers.Authorization = 'Bearer ' + token;
                
                // Ensure token refresh interval is running
                startTokenRefreshInterval();
            } else
            {
                // Stop refresh interval if no token
                stopTokenRefreshInterval();
                
                if ($location.$$path !== "/" &&
                    $location.$$path.search(/(SignUp|ResetPassword|SetPassword|ContactUs|SiteDisabled|unsubscribe)/i) === -1) {
                        logout();
                    }
            }

            return config || $q.when(config);
        },
        
        responseError: function (response) {
            // Check if the error is related to JWT token authentication
            if (response.status === 401 || 
                response.status === 403 ||
                (response.data && response.data.ErrorMessages && 
                 response.data.ErrorMessages.length > 0 && 
                 response.data.ErrorMessages[0].indexOf('JWT') !== -1)) {
                
                // Stop the token refresh interval
                stopTokenRefreshInterval();
                
                // Log the user out when JWT validation fails
                $rootScope.sessionExpired = true;
            }
            
            return $q.reject(response);
        }
    };
}]);

/* ==============================================================================

 AUTHENTICATION SERVICE

 Contains the core authentication related code

 ============================================================================= */
angular.module('core.security').factory('Authentication', ['$q', '$rootScope', 'apiService', '$location', '$analytics', function ($q, $rootScope, apiService, $location, $analytics)
{
    // Function to refresh the JWT token
    function refreshUserToken(forceRefresh) {
        var deferred = $q.defer();
        var hostname = $location.host() || 'default';
        var cookieName = hostname + '.jwt.token';
        var token = Cookies.get(cookieName);
        
        // Only refresh if user is active or force refresh is requested
        if (!$rootScope.userActive && forceRefresh !== true) {
            return deferred.promise;
        }
        
        if (!token) {
            return deferred.promise;
        }
        
        var apiSvcSettings = new ApiServiceSettings(
            {
                method: 'GET',
                url: '../api/RefreshToken',
                params: {}
            }
        );
        
        apiService.execute(apiSvcSettings).then(
            function(data) {
                if (data.Data && data.Data.Token) {

                    var expireDate;

                    // Use server-provided expiration date if available
                    if (data.Data.Expiration) {
                        expireDate = new Date(data.Data.Expiration);
                    } else {
                        // Fall back to client-side timeout if server doesn't provide expiration
                        expireDate = new Date();
                        expireDate.setMilliseconds(expireDate.getMilliseconds() + $rootScope.timeout);
                    }

                    // First remove any existing cookie with proper path
                    Cookies.remove(cookieName, { path: '/', domain: $location.host() });

                    // Put the cookie with consistent options
                    Cookies.set(cookieName, data.Data.Token, { 
                        expires: expireDate,
                        path: '/',
                        domain: $location.host(),
                        sameSite: 'strict' // Prevents CSRF
                    });
                } else {
                    deferred.reject('Invalid token response');
                }
            },
            function(error) {
                console.error("Error refreshing token:", error);
                
                // Clear token cookie if refresh fails with authentication error
                if (error && (error.status === 401 || error.status === 403)) {
                    // Use Cookies API consistently
                    Cookies.remove(cookieName, { path: '/', domain: $location.host() });
                    $rootScope.isAuthenticated = false;
                }
                
                deferred.reject(error);
            }
        );
        
        return deferred.promise;
    }

    return {
        // Expose token refresh function
        refreshToken: refreshUserToken,
        
        isAuthenticated: function ()
        {
            var hostname = $location.host() || 'default';
            var cookieName = hostname + '.jwt.token';
            var deferred = $q.defer(),
                token = Cookies.get(cookieName);

            if (token === undefined)
            {
                $rootScope.isAuthenticated = false;
                deferred.reject('authentication:auth-token not valid');
            }
            else
            {
                $rootScope.isAuthenticated = true;
                deferred.resolve('authentication:auth-token is valid');
            }
            return deferred.promise;
        },
        login: function (userId, password, impersonationGuid)
        {
            var deferred = $q.defer();
            var data = {
                "WebUserId": userId || '',
                "WebUserPassword": password || '',
                'ImpersonationGuid': impersonationGuid || ''
            };
            var apiSvcSettings = new ApiServiceSettings(
                {
                    method: 'POST',
                    url: '../api/Login',
                    data: data
                }
            );
            apiService.execute(apiSvcSettings).then(
                function (data)
                {
                    $rootScope.loginInfo.userId = data.Data.ImpersonatedUserId;
                    $rootScope.loginInfo.userKey = data.Data.ImpersonatedKey;
                    $rootScope.loginInfo.userType = data.Data.ImpersonatedType;
                    $rootScope.loginInfo.impersonatorUserId = data.Data.ImpersonatorUserId;
                    $rootScope.loginInfo.impersonatorUserKey = data.Data.ImpersonatorKey;
                    $rootScope.loginInfo.impersonatorUserType = data.Data.ImpersonatorType;

                    // Create Google Analytics Login event with Web User Key
                    $analytics.eventTrack('Successful Login', { category: 'User Access', label: data.Data.Key });
                    deferred.resolve(data.Data);
                },
                function (data)
                {
                    // Create Google Analytics Login event with Web User Key
                    $analytics.eventTrack('Failed Login', { category: 'User Access', label: data.Errors[0] || 'Valid login credentials required' });
                    deferred.reject(data.ErrorMessages);
                }
            );
            
            return deferred.promise;
        },
        userToken: function ()
        {
            var hostname = $location.host() || 'default';
            var cookieName = hostname + '.jwt.token';
            var token = Cookies.get(cookieName);
            if (token !== undefined)
            {
                return token;
            } else
            {
                $location.path('/');
            }
        }
    };
}]);

/* ==============================================================================

 USER SERVICE

 Contains the core USER related code

 ============================================================================= */
angular.module('core.security')
    .factory('WebUserSvc', ['$q', '$location', '$rootScope', 'apiService', function ($q, $location, $rootScope, apiService)
    {
        var webUser;

        function removeEmptySiteMenuEntries(data)
        {
            if (!data.Data.WebUser || !data.Data.WebUser.CurrentAuthorization || !data.Data.WebUser.CurrentAuthorization.SiteLayout)
            {
                return;
            }

            data.Data.WebUser.CurrentAuthorization.SiteLayout.SubMenu.remove(function (menu)
            {
                return !data.Data.WebUser.CurrentAuthorization.SiteLayout.SideMenu.any({ParentKey:menu.Key});
            });
        }

        function retrieveWebUserFromAPI(user)
        {
            var deferred = $q.defer();
            var apiSvcSettings = new ApiServiceSettings(
                {
                    method: 'GET',
                    url: '../api/GetWebUser/' + user,
                    params: {}
                });
            apiService.execute(apiSvcSettings).then(
                function (data)
                {
                    removeEmptySiteMenuEntries(data);
                    webUser = data.Data.WebUser;
                    deferred.resolve(webUser);
                }
            );
            return deferred.promise;
        }

        function retrieveWebUserFromAPIWithToken(profile) {
            var deferred = $q.defer();
            var apiSvcSettings = new ApiServiceSettings(
                {
                    method: 'POST',
                    url: '../api/GetWebUserByToken/',
                    data: {
                        ProfileKey: profile
                    }
                });
            apiService.execute(apiSvcSettings).then(
                function (data) {
                    removeEmptySiteMenuEntries(data);
                    webUser = data.Data.WebUser;
                    deferred.resolve(webUser);
                }
            );
            return deferred.promise;
        }

        function retrieveSelectedProfileFromAPI(user, profile)
        {
            var deferred = $q.defer();
            var apiSvcSettings = new ApiServiceSettings(
                {
                    method: 'GET',
                    url: '../api/GetWebUserAndProfile/' + user + '/' + profile,
                    params: {}
                });
            apiService.execute(apiSvcSettings).then(
                function (data)
                {
                    removeEmptySiteMenuEntries(data);
                    webUser = data.Data.WebUser;
                    deferred.resolve(webUser);
                }
            );
            return deferred.promise;
        }

        function getWebUser()
        {
            return webUser;
        }

        return {
            getLoggedInWebUserToken: function ()
            {
                var hostname = $location.host() || 'default';
                var cookieName = hostname + '.jwt.token';
                var token = Cookies.get(cookieName);

                if (token === undefined)
                {
                    $rootScope.isAuthenticated = false;
                    return;
                }
                $rootScope.isAuthenticated = true;
                
                return token;
            },
            getWebUserProfile: function () {
                return amplify.store('pnWebProfile');
            },
            getCurrentAuthorization: function () {
                return amplify.store('pnAuthIndex');
            },
            retrieveWebUser: function (user)
            {
                var deferred = $q.defer();
                retrieveWebUserFromAPI(user).then(function (data) {
                    deferred.resolve(data);
                });
                return deferred.promise;
            },
            retrieveWebUserData: function (profile) {
                var deferred = $q.defer();
                retrieveWebUserFromAPIWithToken(profile).then(function (data) {
                    deferred.resolve(data);
                });
                return deferred.promise;
            },
            retrieveWebUserFromAPI: retrieveWebUserFromAPI,
            retrieveWebUserFromAPIWithToken: retrieveWebUserFromAPIWithToken,
            retrieveSelectedProfile: function (user, profile)
            {
                var deferred = $q.defer();
                retrieveSelectedProfileFromAPI(user, profile).then(function (data) {
                    deferred.resolve(data);
                });
                return deferred.promise;
            },
            getWebUserForSignup: function (webUser)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'GET',
                        url: '../api/GetWebUserForSignup/' + webUser.GUID,
                        params: {}
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data.Data);
                    },
                    function (data)
                    {
                        deferred.reject(data.Errors);
                    }
                );
                return deferred.promise;
            },
            signupWebUser: function (webUser)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'POST',
                        url: '../api/SignupWebUser/',
                        data: webUser
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data.Data);
                    },
                    function (data)
                    {
                        deferred.reject(data.Errors);
                    }
                );
                return deferred.promise;
            },
            getWebUserForPasswordReset: function (webUser)
            {
                var deferred = $q.defer(webUser);
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'GET',
                        url: '../api/GetWebUserForPasswordReset/' + webUser.GUID,
                        params: {}
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data.Data);
                    },
                    function (data)
                    {
                        deferred.reject(data.Errors);
                    }
                );
                return deferred.promise;
            },
            verifyWebUserSecretQuestionAnswer: function (webUser)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'GET',
                        url: '../api/VerifyWebUserSecretQuestionAnswer/' + webUser.GUID + '/' + webUser.AnswerClear,
                        params: {}
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data.Data);
                    },
                    function (data)
                    {
                        deferred.reject(data.Errors);
                    }
                );
                return deferred.promise;
            },
            resetWebUserPassword: function (webUser)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'POST',
                        url: '../api/ResetWebUserPassword/',
                        data: webUser
                    }
                );
                apiService.executeAndNotifyOnSuccess(apiSvcSettings).then(
                    function (data)
                    {
                        webUser = data.Data;
                        deferred.resolve(webUser);
                    },
                    function (data)
                    {
                        deferred.reject(data.Errors);
                    }
                );
                return deferred.promise;
            },
            processForgotMyPassword: function (email)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'GET',
                        url: '../api/ProcessWebUserResetPassword/',
                        params: {
                            webUserId: email
                        }
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data);
                    },
                    function (data)
                    {
                        deferred.reject(data.ErrorMessages);
                    }
                );
                return deferred.promise;
            },
            saveWebUser: function (webUser, quiet)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'POST',
                        url: '../api/SaveWebUser/',
                        data: webUser
                    }
                );
                if (quiet)
                {
                    apiService.execute(apiSvcSettings).then(
                        function (data)
                        {
                            webUser = data.Data.WebUser;
                            deferred.resolve(data.Data);
                        },
                        function (data)
                        {
                            deferred.reject(data.Errors);
                        }
                    );
                } else
                {
                    apiService.executeAndNotifyOnSuccess(apiSvcSettings).then(
                        function (data)
                        {
                            webUser = data.Data.WebUser;
                            deferred.resolve(data.Data);
                        },
                        function (data)
                        {
                            deferred.reject(data.Errors);
                        }
                    );
                }
                return deferred.promise;
            },
            validatePasswordComplexity: function (webUserKey, password, isLogin)
            {
                var deferred = $q.defer();
                var apiSvcSettings = new ApiServiceSettings(
                    {
                        method: 'POST',
                        url: '../api/ValidatePasswordComplexity/',
                        data: {
                            WebUserKey: webUserKey,
                            Password: password,
                            IsLogin: isLogin,
                        }
                    }
                );
                apiService.execute(apiSvcSettings).then(
                    function (data)
                    {
                        deferred.resolve(data.Data);
                    },
                    function (data)
                    {
                        deferred.resolve(data.Errors);
                    }
                );
                return deferred.promise;
            },
            getNextPasswordResetDate: function (webUser)
            {
                var one_day = 1000 * 60 * 60 * 24;
                // Convert both dates to milliseconds
                var date1_ms = new Date(Date.parse(webUser.PWCreatedDate));
                var date2_ms = new Date().getTime();
                var difference_ms = date1_ms - date2_ms;
                return Math.round(difference_ms / one_day);
            },
            getWebUser: getWebUser,
            resolveWebUser: function () {
                var deferred = $q.defer();
                var _user = this.getWebUser();
                var _profile = this.getWebUserProfile();

                // We have the user already
                if (!angular.isUndefinedOrNullOrEmpty(_user)) deferred.resolve();

                // Get the user if we can
                else if (!angular.isUndefinedOrNullOrEmpty(_profile))
                    this.retrieveWebUserData(_profile).then(function () { deferred.resolve(); });

                // User is not able to be retrieved
                else deferred.resolve();

                return deferred.promise;
            }
        };
    }]);
